import {
  TableWithPagination,
  ButtonWithIcon,
  ComponentLoader,
  useResponsive,
} from '@clickcease/cc-client-components-lib'
import countries from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en.json'
import React, { useEffect, useMemo, useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import { useTranslation } from 'react-i18next'

import { Download } from '../../assets/icons/Download'
import { FiltersBar } from './components/FiltersBar/FiltersBar'
import { useDownloadBlockedCSV } from '../../hooks/api/useDownloadBlockedCSV'
import { useBMStore } from '../../stores/useBMStore'
import { replaceWWW } from '../../utils/string'

import { Domain } from 'Types/Domain'

import {
  IPsTableWrapper,
  IPsTableTitleWrapper,
  IPsTableTitle,
  StyledTh,
  StyledTr,
  StyledTd,
  FlagStyles,
} from './DetailedReport.styled'
import { useBlockedQuery } from '../../hooks/api/useBlockedQuery'

interface IPsTableProps {
  locale?: string
}

countries.registerLocale(enLocale)

export const IPsTable: React.FC<IPsTableProps> = ({ locale }) => {
  const { t } = useTranslation('botMitigation')
  const [currentPage, setCurrentPage] = useState(1)
  const paginationLimit = 100
  const offset = Math.floor((currentPage - 1) / 3)
  const { data: blocked, isLoading } = useBlockedQuery(offset)
  const { downloadBlockedCSV } = useDownloadBlockedCSV(offset)
  const domains = useBMStore((state) => state.domains)
  const advancedFilters = useBMStore((state) => state.advancedFilters)
  const { isLaptop } = useResponsive()

  const blockedIps = useMemo(() => blocked?.blockedIps || [], [blocked])
  const totalCount = useMemo(() => blocked?.total || 0, [blocked])
  const pageCount = Math.ceil(totalCount / paginationLimit)

  const paginatedIps = useMemo(() => {
    const startIndex = ((currentPage - 1) % 3) * paginationLimit
    const endIndex = startIndex + paginationLimit
    return blockedIps.slice(startIndex, endIndex)
  }, [blockedIps, currentPage, paginationLimit])

  useEffect(() => {
    setCurrentPage(1)
  }, [advancedFilters])

  const columnNames = [
    <StyledTh key="ip" firstOfKind>
      {t('ipsTableColumnNames.ip')}
    </StyledTh>,
    <StyledTh key="lastSeen">{t('ipsTableColumnNames.lastSeen')}</StyledTh>,
    <StyledTh key="threatGroup">{t('ipsTableColumnNames.threatGroup')}</StyledTh>,
    <StyledTh key="threatType">{t('ipsTableColumnNames.threatType')}</StyledTh>,
    <StyledTh key="actionTaken">{t('ipsTableColumnNames.actionTaken')}</StyledTh>,
    <StyledTh key="country">{t('ipsTableColumnNames.country')}</StyledTh>,
    <StyledTh key="domain">{t('ipsTableColumnNames.domain')}</StyledTh>,
    <StyledTh key="url">{t('ipsTableColumnNames.url')}</StyledTh>,
  ]

  const formatDate = useMemo(() => {
    return (dateString: string) => {
      try {
        const date = new Date(dateString)
        const formattedDate = new Intl.DateTimeFormat(locale ?? 'en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(date)

        const formattedTime = date.toLocaleTimeString(locale ?? 'en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        })

        return `${formattedDate} ${formattedTime}`
      } catch (error) {
        return dateString
      }
    }
  }, [locale])

  const getCountryName = (code: string) => {
    return countries.getName(code.toUpperCase(), 'en', { select: 'alias' }) || code
  }

  const isNotWordPressDomain = (domain: string, domains: Domain[]) => {
    domain = replaceWWW(domain)

    const domainsWithWordpress =
      domains
        ?.filter((domain) => domain.botZappingPluginActivated)
        .map((domain) => replaceWWW(domain.address)) || []

    return !domainsWithWordpress.includes(domain)
  }

  const body = useMemo(() => {
    return paginatedIps.map((item, index) => {
      const monitored = isNotWordPressDomain(item.domain, domains) || item.isMonitoring
      const actionTaken = monitored ? t('ipsTable.detected') : t('ipsTable.blocked')
      return (
        <StyledTr key={index} firstRow={index === 0}>
          <StyledTd title={item.ip} modifiedPadding>
            {item.ip}
          </StyledTd>
          <StyledTd title={formatDate(item.lastClick)}>{formatDate(item.lastClick)}</StyledTd>
          <StyledTd title={item.threatGroup}>{item.threatGroup}</StyledTd>
          <StyledTd title={item.threatType}>{item.threatType}</StyledTd>
          <StyledTd title={actionTaken}>{actionTaken}</StyledTd>
          <StyledTd title={getCountryName(item.country)}>
            <ReactCountryFlag countryCode={item.country} svg style={FlagStyles} />
            {getCountryName(item.country)}
          </StyledTd>
          <StyledTd title={replaceWWW(item.domain)}>{replaceWWW(item.domain)}</StyledTd>
          <StyledTd
            title={(() => {
              try {
                return new URL(item.url).pathname
              } catch (error) {
                return item.url || '-'
              }
            })()}
          >
            {(() => {
              try {
                return new URL(item.url).pathname
              } catch (error) {
                return item.url || '-'
              }
            })()}
          </StyledTd>
        </StyledTr>
      )
    })
  }, [paginatedIps, formatDate, domains, t])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const downloadCsv = async () => {
    try {
      const data = await downloadBlockedCSV()
      if (data && data.url) {
        window.open(data.url, '_blank')
      }
    } catch (error) {
      console.error('Failed to download CSV:', error)
    }
  }

  return (
    <IPsTableWrapper isLaptop={isLaptop} isLoadingBlockedQuery={isLoading}>
      <IPsTableTitleWrapper>
        <IPsTableTitle>{t('ipsTable.title')}</IPsTableTitle>
        <ButtonWithIcon
          variant="ghost"
          text={t('ipsTable.download')}
          iconLeft={<Download width={'14px'} height={'14px'} />}
          onClick={downloadCsv}
        />
      </IPsTableTitleWrapper>
      <FiltersBar locale={locale} />
      {isLoading ? (
        <ComponentLoader />
      ) : (
        <TableWithPagination
          columnsNames={columnNames}
          body={body}
          pageCount={pageCount}
          currentPage={currentPage}
          totalItems={totalCount}
          limitPerPage={paginationLimit}
          onFirstPage={() => handlePageChange(1)}
          onLastPage={() => handlePageChange(pageCount)}
          onNextPage={() => handlePageChange(Math.min(currentPage + 1, pageCount))}
          onPreviousPage={() => handlePageChange(Math.max(currentPage - 1, 1))}
        />
      )}
    </IPsTableWrapper>
  )
}
